import axios from 'axios';
import qs from 'qs';
import {
  SFDC_API_PATH,
  SFDC_API_TOKEN,
} from '@/api/path';

export const userRequest = axios.create({
  baseURL: '',
  headers: {
    'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
  },
  withCredentials: process.env.NODE_ENV === 'development' ? false : true,
});
export const postFunc = (path, data, headers = {}) => userRequest.post(`${ SFDC_API_PATH }${ path }`, qs.stringify(data), { headers });
export const getFunc = (path, headers = {}) => userRequest.get(`${SFDC_API_PATH}${path}`, { headers });

/**
 * 登入
 * Use email and password to login
 *
 * @param {Object} data
 * @param {string} data.Email
 * @param {string} data.Password
 * @return {object} data
 * @return {('Fail'|'Success')} data.rtnStatus
 */
export const loginVerify = async(data) => await postFunc('/loginVerify.php', data);

/**
 * 登出
 *
 * @return {string} - rtnStatus
 */
export const logout = async() => await getFunc('/logout.php');

/**
 * 重設密碼
 *
 * if not logged-in, simply send Email, and the password will be reset to random.
 * if is logged-in, both Email and Password should be provided.
 *
 * @param {Object} data
 * @param {string} data.Email
 * @param {Object} data.Password
 * @param {Object} data.NewPassword
 * @return {('Fail'|'Success')} - rtnStatus
 */
export const resetPassword = async(data) => await postFunc('/updateLoginAccountPassword.php', data);

/**
 * 取得登入狀態
 * Access level: all
 *
 * @return {Object} data
 * @return {Boolean} data.rtnStatus - indicating whether the current session is logged-in as a normal user
 * @return {('google'|'facebook'|'email'|'phone'|"")} data.accountType - the way the current session is logged-in. AccountType should be '' when rtnStatus is false. One exception is that when account type is 'phone', rtnStatus is still false, because SMS verification is not considered a real login, but a temporary access to certain restricted data.
 */
export const getLoginStatus = async() => await getFunc('/loginStatus.php');

/**
 * For logged-in user to get his/her own contact data
 * Access level: normal login account as well as SMS verification
 *
 * @param {Boolean} verboseMode - `false` get cached data, `true` get complete data
 */
export const getContactData = async(verboseMode) => await postFunc('/getContactData.php', verboseMode ? { verbose: true } : '');

/**
 * 取得捐款記錄
 */
export const getMemberAreaData = async() => await getFunc('/getMemberAreaData.php');

/**
 * 取得定期定額捐款 order id
 */
export const getRecurringDonationOrderNumber = async() => {
  const headers = {
    'token': SFDC_API_TOKEN,
  };
  return await getFunc('/newOrderNumber.php', headers);
};

/**
 * 取得定期定額捐款資料
 *
 * For logged-in user to get his/her recurring donations
 * The accountType returned by loginStatus must not be ""
 */
export const getRecurringDonations = async() => await getFunc('/getRecurringDonations.php');

/**
 * 更新定期定額捐款資料
 * For logged-in user to update his/her recurring donations
 * Access level: normal login account
 *
 * @param {Object} data - contact data and its updated data
 * @param {} data.recurDonationID -  required, indicating which record to update
 * @param {} data.recurDonationCreditCardExpire -  optional
 * @param {} data.recurDonationCreditCardNo -  optional
 * @param {} data.recurDonationInstallmentNumber -  optional
 * @param {} data.recurDonationInstallmentAmount -  optional
 * @param {} data.recurDonationCreditCardCCV -  optional
 * @param {} data.recurDonationCreditCardOwnerName -  optional
 */
export const updateRecurringDonation = async(data) => await postFunc('/updateRecurringDonation.php', data);

/**
 * For user without an account to get access to his/her donation info
 * Access level: all
 *
 * @param {Object} data - contact data and its updated data
 * @param {string} data.mobilePhone - required
 * @param {string} data.firstName - required
 * @param {string} data.lastName - required
 * @return {Object} data
 * @return {('Fail'|'Success')} data.rtnStatus
 * @return {string} data.msg
 */
export const phoneSMSVerificationSend = async(data) => await postFunc('/phoneSMSVerificationSend.php', data);

/**
 * SMS 登入
 *
 * Use the verification code sent by phoneSMSVerificationSend API to "login"
 * Access level: all
 *
 * @param {string} data.verificationCode
 * @return {Object} data
 * @return {('Failed'|'Success'|'Expired'|"")} data.rtnStatus
 *
 * @other:
 * after logged-in, loginStatus API will return "{rtnStatus: false, accountType: 'phone' }"
 */
export const phoneSMSVerify = async(data) => await postFunc('/phoneSMSVerify.php', data);

/**
 * 電子收據補發
 * resend receipt of a payment
 * Access level: All
 *
 * @param {Object} data
 * @param {string} data.Id - payment id, required
 * @return {Object} data
 * @return {('Failed'|'Success')} data.rtnStatus
 * @return {Number} data.response
 */
export const resendPaymentReceipt = async(data) => await postFunc('/resendPaymentReceipt.php', data);

/*
 * resend 代繳款項代碼 of a payment
 * Access level: All
 *
 * @param {Object} data
 * @param {string} data.Id - payment id, required
 */
export const resendPaymentInfo = async(data) => await postFunc('/resendPaymentInfo.php', data);

export const newOpportunity = async(data) => await postFunc('/newOpportunity.php', data);

export const updateOpportunityOrderInfo = async(data) => await postFunc('/updateOpportunityOrderInfo.php', data);

export const updateOppoIDandTransactionID = async(data) => await postFunc('/updateOPP3rdTransactionID.php', data);

export const getSingleDonationAmount = async(data) => {
  const headers = {
    'token': SFDC_API_TOKEN,
  };
  return await postFunc('/getOppAmount.php', data, headers);
};

export const getRecurringDonationAmount = async(data) => {
  const headers = {
    'token': SFDC_API_TOKEN,
  };
  return await postFunc('/getRecIdAmount.php', data, headers);
};

export const validateCampaignId = async(data) => await postFunc('/validateCampaignId.php', data);

export const newRecurringDonation = async(data) => await postFunc('/newRecurringDonation.php', data);
